/**
 * Beaute Plus — Product zoom modal navigation
 * Handles Jo Malone-style lightbox:
 *  - Thumbnail click → switch active main image
 *  - Bottom prev/next chevrons → cycle main image
 *  - Left ^/v buttons → scroll thumbnail list
 *  - Sync active thumbnail with active main image
 *
 * Dawn's product-modal.js handles `.active` class on data-media-id elements.
 * We extend it: thumbnails carry data-media-target matching data-media-id,
 * and active class is mirrored on the matching thumb.
 */
(function () {
  const THUMB_HEIGHT = 80;
  const THUMB_GAP = 12;
  const SCROLL_STEP = (THUMB_HEIGHT + THUMB_GAP) * 3;

  function setActive(modal, mediaId) {
    // Sync main image active state
    const mainItems = modal.querySelectorAll('.bp-modal__main [data-media-id]');
    mainItems.forEach((item) => {
      const id = item.getAttribute('data-media-id');
      item.classList.toggle('active', id === mediaId);
    });

    // Sync thumbnail active state
    const thumbs = modal.querySelectorAll('.bp-modal__thumb');
    thumbs.forEach((thumb) => {
      const target = thumb.getAttribute('data-media-target');
      thumb.classList.toggle('is-active', target === mediaId);
    });

    // Scroll active thumbnail into view
    const activeThumb = modal.querySelector('.bp-modal__thumb.is-active');
    if (activeThumb) {
      activeThumb.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }

    // Update prev/next disabled state
    updateMainNavState(modal);
  }

  function getCurrentMediaId(modal) {
    const active = modal.querySelector('.bp-modal__main [data-media-id].active');
    return active ? active.getAttribute('data-media-id') : null;
  }

  function getOrderedMediaIds(modal) {
    return Array.from(modal.querySelectorAll('.bp-modal__main [data-media-id]')).map((el) =>
      el.getAttribute('data-media-id')
    );
  }

  function navigateMain(modal, direction) {
    const ids = getOrderedMediaIds(modal);
    if (!ids.length) return;
    const current = getCurrentMediaId(modal);
    const idx = ids.indexOf(current);
    let next = idx + direction;
    if (next < 0) next = 0;
    if (next >= ids.length) next = ids.length - 1;
    setActive(modal, ids[next]);
  }

  function updateMainNavState(modal) {
    const ids = getOrderedMediaIds(modal);
    if (!ids.length) return;
    const current = getCurrentMediaId(modal);
    const idx = ids.indexOf(current);
    const prevBtn = modal.querySelector('.bp-modal__main-nav-btn--prev');
    const nextBtn = modal.querySelector('.bp-modal__main-nav-btn--next');
    if (prevBtn) prevBtn.toggleAttribute('disabled', idx <= 0);
    if (nextBtn) nextBtn.toggleAttribute('disabled', idx >= ids.length - 1);
  }

  function updateThumbNavState(modal) {
    const list = modal.querySelector('.bp-modal__thumb-list');
    if (!list) return;
    const upBtn = modal.querySelector('.bp-modal__thumb-nav--up');
    const downBtn = modal.querySelector('.bp-modal__thumb-nav--down');
    const maxScroll = Math.max(0, list.scrollHeight - list.clientHeight);
    if (upBtn) upBtn.toggleAttribute('disabled', list.scrollTop <= 0);
    if (downBtn) downBtn.toggleAttribute('disabled', list.scrollTop >= maxScroll - 1);
  }

  function setupModal(modal) {
    if (modal.dataset.bpInit === '1') return;
    modal.dataset.bpInit = '1';

    // Dawn's ModalDialog (media-modal) closes on ANY pointerup whose target isn't
    // inside <deferred-media> or <product-model>. That breaks our nav buttons,
    // thumbnails, and background clicks inside the lightbox.
    // Solution: capture-phase listener that stops propagation BEFORE Dawn's
    // bubble-phase listener fires. Close now happens ONLY via the X button
    // (separate click handler) or ESC (separate keyup handler).
    modal.addEventListener(
      'pointerup',
      (e) => {
        // If the target is the close X button, let it pass through (its own
        // click handler triggers hide()). Otherwise block Dawn's auto-close.
        if (e.target.closest('.bp-modal__close, [id^="ModalClose-"]')) return;
        e.stopImmediatePropagation();
      },
      true // capture phase — runs before Dawn's bubble listener
    );

    const thumbList = modal.querySelector('.bp-modal__thumb-list');

    // Thumbnail clicks → switch active main image
    modal.querySelectorAll('.bp-modal__thumb').forEach((thumb) => {
      thumb.addEventListener('click', (e) => {
        e.preventDefault();
        const target = thumb.getAttribute('data-media-target');
        if (target) setActive(modal, target);
      });
    });

    // Bottom prev/next nav
    const prevBtn = modal.querySelector('.bp-modal__main-nav-btn--prev');
    const nextBtn = modal.querySelector('.bp-modal__main-nav-btn--next');
    if (prevBtn) {
      prevBtn.addEventListener('click', (e) => {
        e.preventDefault();
        navigateMain(modal, -1);
      });
    }
    if (nextBtn) {
      nextBtn.addEventListener('click', (e) => {
        e.preventDefault();
        navigateMain(modal, 1);
      });
    }

    // Up/Down thumbnail scroll buttons
    const upBtn = modal.querySelector('.bp-modal__thumb-nav--up');
    const downBtn = modal.querySelector('.bp-modal__thumb-nav--down');
    if (upBtn && thumbList) {
      upBtn.addEventListener('click', (e) => {
        e.preventDefault();
        thumbList.scrollBy({ top: -SCROLL_STEP, behavior: 'smooth' });
      });
    }
    if (downBtn && thumbList) {
      downBtn.addEventListener('click', (e) => {
        e.preventDefault();
        thumbList.scrollBy({ top: SCROLL_STEP, behavior: 'smooth' });
      });
    }

    // Update thumb nav disabled state on scroll
    if (thumbList) {
      thumbList.addEventListener('scroll', () => updateThumbNavState(modal), { passive: true });
      requestAnimationFrame(() => updateThumbNavState(modal));
    }

    // Keyboard navigation: ArrowLeft/Right within modal
    modal.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        navigateMain(modal, -1);
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        navigateMain(modal, 1);
      }
    });
  }

  function initAll() {
    document.querySelectorAll('product-modal.bp-modal').forEach(setupModal);
  }

  // Observe modal open state — Dawn opens via toggling [open] attribute
  function observeModalOpens() {
    document.querySelectorAll('product-modal.bp-modal').forEach((modal) => {
      if (modal.dataset.bpObserved === '1') return;
      modal.dataset.bpObserved = '1';

      const observer = new MutationObserver(() => {
        if (modal.hasAttribute('open')) {
          // Modal just opened — sync active state from currently .active item
          requestAnimationFrame(() => {
            const currentId = getCurrentMediaId(modal);
            if (currentId) setActive(modal, currentId);
            else {
              // Fallback: activate first media
              const ids = getOrderedMediaIds(modal);
              if (ids.length) setActive(modal, ids[0]);
            }
          });
        }
      });
      observer.observe(modal, { attributes: true, attributeFilter: ['open'] });
    });
  }

  function init() {
    initAll();
    observeModalOpens();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  document.addEventListener('shopify:section:load', init);
})();
